import { useState, useEffect, useRef, useCallback } from 'react';
import { FiSearch, FiX, FiClock, FiTrendingUp, FiChevronDown } from 'react-icons/fi';
import _ from 'lodash';

const API_KEY = 'a1e72fd93ed59f56e6332813b9f8dcae';
const BASE_URL = 'https://api.themoviedb.org/3';
const IMG_URL = 'https://image.tmdb.org/t/p/original';

export default function SearchModal({ onClose, onItemSelect, lastSearch = { query: '', results: [] } }) {
  const [query, setQuery] = useState(lastSearch.query);
  const [results, setResults] = useState(lastSearch.results);
  const [recommendations, setRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const inputRef = useRef(null);
  const resultsContainerRef = useRef(null);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Focus input and restore search if coming back from detail view
  useEffect(() => {
    inputRef.current?.focus();
    if (lastSearch.query && lastSearch.results.length === 0) {
      // Only trigger search if we have a query but no results
      searchTMDB(lastSearch.query);
    } else if (!lastSearch.query) {
      // Load recommendations if no search query
      loadRecommendations();
    }
  }, []);

  const loadRecommendations = useCallback(async () => {
    setIsLoadingRecommendations(true);
    try {
      // Get trending movies and TV shows for the week (closest to monthly data available)
      const [moviesRes, tvRes] = await Promise.all([
        fetch(`${BASE_URL}/trending/movie/week?api_key=${API_KEY}`),
        fetch(`${BASE_URL}/trending/tv/week?api_key=${API_KEY}`)
      ]);
      
      const moviesData = await moviesRes.json();
      const tvData = await tvRes.json();
      
      // Combine and sort by popularity, limit to top 20
      const combined = [
        ...moviesData.results.slice(0, 10),
        ...tvData.results.slice(0, 10)
      ]
        .filter(item => item.poster_path)
        .sort((a, b) => b.popularity - a.popularity)
        .slice(0, 20);
      
      setRecommendations(combined);
    } catch (error) {
      console.error("Recommendations error:", error);
    } finally {
      setIsLoadingRecommendations(false);
    }
  }, []);

  // Calculate relevance score based on search query match
  const calculateRelevance = (item, searchQuery) => {
    if (!searchQuery.trim()) return 0;
    
    const query = searchQuery.toLowerCase();
    const title = (item.title || item.name || '').toLowerCase();
    const overview = (item.overview || '').toLowerCase();
    
    let score = 0;
    
    // Exact title match gets highest score
    if (title === query) score += 100;
    
    // Title starts with query gets high score
    else if (title.startsWith(query)) score += 80;
    
    // Title contains query gets medium score
    else if (title.includes(query)) score += 60;
    
    // Overview contains query gets lower score
    else if (overview.includes(query)) score += 20;
    
    // Boost score for more recent releases
    const releaseDate = item.release_date || item.first_air_date;
    if (releaseDate) {
      const year = parseInt(releaseDate.substring(0, 4));
      const currentYear = new Date().getFullYear();
      const yearDiff = currentYear - year;
      if (yearDiff <= 2) score += 10; // Recent releases get small boost
    }
    
    return score;
  };

  const searchTMDB = useCallback(_.debounce(async (searchQuery, page = 1, append = false) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setCurrentPage(1);
      setTotalPages(0);
      return;
    }

    if (page === 1) {
      setIsLoading(true);
      setResults([]);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const res = await fetch(
        `${BASE_URL}/search/multi?api_key=${API_KEY}&query=${encodeURIComponent(searchQuery)}&page=${page}`
      );
      const data = await res.json();
      
      // Filter and calculate relevance scores
      const filteredResults = data.results
        .filter(item => item.poster_path)
        .map(item => ({
          ...item,
          relevanceScore: calculateRelevance(item, searchQuery)
        }))
        .sort((a, b) => {
          // First, prioritize Best Match items (relevance >= 60)
          const aBestMatch = a.relevanceScore >= 60;
          const bBestMatch = b.relevanceScore >= 60;
          
          if (aBestMatch && !bBestMatch) return -1;
          if (!aBestMatch && bBestMatch) return 1;
          
          // Within same category (both best match or both not), sort by relevance score first
          if (b.relevanceScore !== a.relevanceScore) {
            return b.relevanceScore - a.relevanceScore;
          }
          
          // Then by popularity as tiebreaker
          return (b.popularity || 0) - (a.popularity || 0);
        });
      
      setResults(prevResults => append ? [...prevResults, ...filteredResults] : filteredResults);
      setCurrentPage(page);
      setTotalPages(data.total_pages);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, 300), []);

  useEffect(() => {
    return () => {
      searchTMDB.cancel();
    };
  }, [searchTMDB]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setCurrentPage(1);
    searchTMDB(value);
  };

  const handleShowMore = () => {
    if (currentPage < totalPages && !isLoadingMore) {
      searchTMDB(query, currentPage + 1, true);
    }
  };

  const handleItemSelect = (item) => {
    // Pass the current search state up before closing
    onItemSelect(item, { query, results });
  };

  const displayItems = query.trim() ? results : recommendations;
  const showLoading = query.trim() ? isLoading : isLoadingRecommendations;
  const showEmptyState = query.trim() && results.length === 0 && !isLoading;
  const headerTitle = query.trim() ? "Search Results" : "Most Watched This Month";
  const canShowMore = query.trim() && currentPage < totalPages && !isLoading;

  // Count best matches for display
  const bestMatchCount = query.trim() ? results.filter(item => item.relevanceScore >= 60).length : 0;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-90 flex flex-col z-50 overflow-hidden"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="p-4 md:p-8 h-full flex flex-col">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold">Search</h1>
          <button 
            onClick={onClose}
            className="text-white hover:text-gray-300 p-2"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        <div className="relative mb-8">
          <div className="relative">
            <FiSearch className="absolute left-3 top-3.5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleInputChange}
              placeholder="Search movies, TV shows..."
              className="w-full pl-10 pr-4 py-3 bg-gray-800 rounded text-white text-lg focus:outline-none focus:ring-2 focus:ring-red-600"
              autoFocus
            />
          </div>
        </div>

        {/* Section Header */}
        {(displayItems.length > 0 || showLoading) && (
          <div className="flex items-center mb-6">
            {!query.trim() && (
              <FiTrendingUp className="w-5 h-5 mr-2 text-red-600" />
            )}
            <h2 className="text-lg font-semibold text-white">
              {headerTitle}
            </h2>
            {!query.trim() && (
              <span className="ml-2 text-sm text-gray-400">
                • Trending now
              </span>
            )}
            {query.trim() && results.length > 0 && (
              <span className="ml-2 text-sm text-gray-400">
                • {results.length} results
                {bestMatchCount > 0 && ` (${bestMatchCount} best matches)`}
                {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
              </span>
            )}
          </div>
        )}

        <div 
          ref={resultsContainerRef}
          className="flex-1 overflow-y-auto scrollbar-hide"
        >
          {showLoading ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 pb-8">
                {displayItems.map((item, index) => {
                  const releaseDate = item.release_date || item.first_air_date;
                  const currentYear = new Date().getFullYear();
                  const isRecent = releaseDate && releaseDate.startsWith(currentYear.toString());
                  const isTrending = !query.trim() && index < 3; // Mark top 3 as trending when showing recommendations
                  const isHighRelevance = query.trim() && item.relevanceScore >= 60; // High relevance for search results
                  
                  return (
                    <div 
                      key={`${item.id}-${index}`} 
                      className="group relative rounded overflow-hidden transition-transform duration-300 hover:scale-105 hover:z-10 cursor-pointer"
                      onClick={() => handleItemSelect(item)}
                    >
                      <img 
                        src={`${IMG_URL}${item.poster_path}`}
                        alt={item.title || item.name}
                        className="w-full h-auto object-cover"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-300 flex flex-col justify-between p-2">
                        <div className="flex flex-wrap gap-1">
                          {isHighRelevance && (
                            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs px-2 py-1 rounded-full font-semibold">
                              Best Match
                            </div>
                          )}
                          {isTrending && (
                            <div className="bg-gradient-to-r from-red-600 to-orange-500 text-white text-xs px-2 py-1 rounded-full flex items-center">
                              <FiTrendingUp className="mr-1" /> #{index + 1}
                            </div>
                          )}
                          {isRecent && !isTrending && !isHighRelevance && (
                            <div className="bg-red-600 text-white text-xs px-2 py-1 rounded-full flex items-center">
                              <FiClock className="mr-1" /> New
                            </div>
                          )}
                        </div>
                        <div className="mt-auto">
                          <h3 className="text-white text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300 truncate">
                            {item.title || item.name}
                          </h3>
                          <p className="text-gray-300 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            {releaseDate?.substring(0, 4) || 'N/A'}
                          </p>
                          {!query.trim() && (
                            <p className="text-orange-400 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              Trending
                            </p>
                          )}
                          {isHighRelevance && (
                            <p className="text-green-400 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              Perfect match
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Show More Button */}
              {canShowMore && (
                <div className="flex justify-center py-6">
                  <button
                    onClick={handleShowMore}
                    disabled={isLoadingMore}
                    className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white px-6 py-3 rounded-lg transition-colors duration-200"
                  >
                    {isLoadingMore ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                        <span>Loading...</span>
                      </>
                    ) : (
                      <>
                        <span>Show More Results</span>
                        <FiChevronDown className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}

          {showEmptyState && (
            <div className="text-center py-16">
              <h3 className="text-xl text-gray-400">No results found for "{query}"</h3>
              <p className="text-gray-500">Try different keywords</p>
            </div>
          )}

          {!query.trim() && recommendations.length === 0 && !isLoadingRecommendations && (
            <div className="text-center py-16">
              <FiTrendingUp className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl text-gray-400">Unable to load recommendations</h3>
              <p className="text-gray-500">Try searching for something specific</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}